import React from 'react'
import { MessageSquare, ChevronRight, Building2, Users, CheckCircle2, UserPlus } from 'lucide-react'
import { Person, AssistantConnection } from '../types/profile'
import { Button } from './ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { cn } from '../lib/utils'

interface OrgListProps {
    people: Person[]
    selectedPersonId?: string
    onPersonSelect?: (person: Person) => void
    onMessage?: (person: Person) => void
    currentUserAccessList?: AssistantConnection[]
}

// Helper to find manager name
const findManagerName = (people: Person[], reportsTo?: string): string | null => {
    if (!reportsTo) return null
    const manager = people.find(p => p.id === reportsTo || p.name === reportsTo)
    return manager?.name || null
}

export const OrgList: React.FC<OrgListProps> = ({
    people,
    selectedPersonId,
    onPersonSelect,
    onMessage,
    currentUserAccessList = [],
}) => {
    if (people.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No people found</p>
            </div>
        )
    }

    // Group by department for better organization
    const groupedByDepartment = people.reduce((acc, person) => {
        const dept = person.department || 'Other'
        if (!acc[dept]) {
            acc[dept] = []
        }
        acc[dept].push(person)
        return acc
    }, {} as Record<string, Person[]>)

    // Sort departments and people within departments
    const sortedDepartments = Object.keys(groupedByDepartment).sort()
    sortedDepartments.forEach(dept => {
        groupedByDepartment[dept].sort((a, b) => a.name.localeCompare(b.name))
    })

    return (
        <Accordion type="multiple" defaultValue={sortedDepartments} className="space-y-2">
            {sortedDepartments.map((department) => (
                <AccordionItem key={department} value={department} className="border rounded-lg px-3 py-1 bg-card">
                    <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex items-center gap-2 flex-1">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <h3 className="font-semibold text-sm text-foreground">{department}</h3>
                            <span className="text-xs text-muted-foreground ml-auto">
                                {groupedByDepartment[department].length} {groupedByDepartment[department].length === 1 ? 'person' : 'people'}
                            </span>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        {/* People List */}
                        <div className="space-y-1 pt-2">
                            {groupedByDepartment[department].map((person) => {
                                const isSelected = selectedPersonId === person.id
                                const managerName = findManagerName(people, person.reportsTo)
                                const hasAccess = currentUserAccessList.some(
                                    connection => connection.personName === person.name
                                )

                                return (
                                    <div
                                        key={person.id}
                                        onClick={() => onPersonSelect?.(person)}
                                        className={cn(
                                            'group flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer',
                                            'hover:bg-accent hover:border-accent-foreground/20',
                                            isSelected && 'bg-primary/5 border-primary shadow-sm'
                                        )}
                                    >
                                        {/* Avatar */}
                                        <div className={cn(
                                            'rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0',
                                            'h-10 w-10'
                                        )}>
                                            <span className="text-primary font-medium text-sm">
                                                {person.name
                                                    .split(' ')
                                                    .map(n => n[0])
                                                    .join('')
                                                    .toUpperCase()
                                                    .slice(0, 2)}
                                            </span>
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className={cn(
                                                            'font-semibold truncate',
                                                            isSelected && 'text-primary'
                                                        )}>
                                                            {person.name}
                                                        </h4>
                                                        {/* Access Indicator */}
                                                        <TooltipProvider>
                                                            {hasAccess ? (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500 flex-shrink-0" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>You have access</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <UserPlus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Request access</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                        </TooltipProvider>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {person.title}
                                                    </p>
                                                    {managerName && (
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                            Reports to: <span className="font-medium">{managerName}</span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {hasAccess && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onMessage?.(person)
                                                    }}
                                                    aria-label="Message"
                                                >
                                                    <MessageSquare className="h-4 w-4" />
                                                </Button>
                                            )}
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    )
}

